import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Grid,
  Box,
  Checkbox,
  ListItemText,
  ListSubheader,
  TextField,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast } from 'react-toastify';
import { sortBuildings, formatBuildingLabel } from '../../../../utils/buildingUtils';
import styles from './GPExtensionModal.module.css';
import GPExtensionResultToast from './GPExtensionResultToast';

// Helper to resolve base URL
const getBaseUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  return url ? url.trim().replace(/\/+$/, '') : '';
};

// Helper to resolve auth token
const getAuthToken = () => {
  return (
    Cookies.get('Token') ||
    Cookies.get('token') ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '') ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('Token') : '') ||
    ''
  );
};

// Fallback generator for academic year options
const generateAcademicYears = () => {
  const currentYear = new Date().getFullYear();
  const start = currentYear - 5;
  const end = currentYear + 5;
  const years = [];
  for (let y = start; y <= end; y++) {
    const nextYear2Digits = String(y + 1).slice(-2);
    years.push(`${y}-${nextYear2Digits}`);
  }
  return years;
};

const getBuildingValue = (building) => {
  const value = building?._id ?? building?.id ?? building?.buildingId ?? '';
  return String(value);
};

const getBuildingLabel = (building) => {
  const name = building?.name || building?.buildingName || building?.title;
  const buildingNo = building?.building_no;

  let label = '';
  if (name && buildingNo && String(name) !== String(buildingNo)) {
    label = `${name} (${buildingNo})`;
  } else {
    label = String(name || buildingNo || 'Unnamed Building');
  }
  return formatBuildingLabel(label);
};

// Extract students array from any API payload structure
const extractStudentList = (resData) => {
  if (!resData) return [];
  if (Array.isArray(resData)) return resData;
  if (Array.isArray(resData?.data)) return resData.data;
  if (Array.isArray(resData?.data?.students)) return resData.data.students;
  if (Array.isArray(resData?.data?.studentList)) return resData.data.studentList;
  if (Array.isArray(resData?.data?.data)) return resData.data.data;
  if (Array.isArray(resData?.data?.passes)) return resData.data.passes;
  if (Array.isArray(resData?.data?.records)) return resData.data.records;
  if (Array.isArray(resData?.students)) return resData.students;
  if (Array.isArray(resData?.studentList)) return resData.studentList;
  if (Array.isArray(resData?.passes)) return resData.passes;
  if (resData?.data && typeof resData.data === 'object') {
    const found = Object.values(resData.data).find(Array.isArray);
    if (found) return found;
  }
  const topFound = Object.values(resData).find(Array.isArray);
  if (topFound) return topFound;
  return [];
};

const getStudentId = (s) => {
  if (!s) return '';
  if (typeof s === 'string') return s;
  const id =
    s._id ||
    s.id ||
    s.studentId ||
    s.userId ||
    s.student?._id ||
    s.student?.id ||
    s.student?.studentId ||
    s.student?.userId ||
    s.UID ||
    s.uid ||
    '';
  return String(id);
};

const getStudentName = (s) => {
  if (!s) return 'Unknown Student';
  if (s.studentFullName) return s.studentFullName;
  if (s.name) return s.name;
  if (s.studentName) return s.studentName;
  if (s.fullName) return s.fullName;
  if (s.student?.studentFullName) return s.student.studentFullName;
  if (s.student?.name) return s.student.name;
  if (s.student?.studentName) return s.student.studentName;
  if (s.firstName || s.lastName) return [s.firstName, s.lastName].filter(Boolean).join(' ');
  if (s.student?.firstName || s.student?.lastName) return [s.student.firstName, s.student.lastName].filter(Boolean).join(' ');
  return s.UID || s.uid || 'Student';
};

const getStudentUID = (s) => {
  if (!s) return '';
  return (
    s.UID ||
    s.uid ||
    s.student?.UID ||
    s.student?.uid ||
    s.enrollmentNo ||
    s.student?.enrollmentNo ||
    s.rollNo ||
    s.regNo ||
    ''
  );
};

const DROPDOWN_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 280,
      minHeight: 100,
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    }
  }
};

const selectSx = {
  borderRadius: '10px',
  backgroundColor: '#ffffff',
  height: '46px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#94a3b8'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#644ee5',
    borderWidth: '1.5px'
  },
  '&.Mui-disabled': {
    backgroundColor: '#f8fafc',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0'
    }
  },
  '& .MuiSelect-select': {
    py: '10px',
    fontSize: '14px',
    color: '#1e293b'
  }
};

const GPExtensionModal = ({ open, onClose, onRefresh }) => {
  // Form values
  const [category, setCategory] = useState('');
  const [selectedBuildingIds, setSelectedBuildingIds] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');

  // Async data states
  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  const [academicYears, setAcademicYears] = useState([]);

  // Calculate today's date in YYYY-MM-DD to restrict future date selection
  const todayStr = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Form submission and validation states
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset all state when modal opens, and fetch academic years from filters API
  useEffect(() => {
    if (open) {
      setCategory('');
      setSelectedBuildingIds([]);
      setSelectedDepartments([]);
      setSelectedSessions([]);
      setSelectedStudentIds([]);
      setDate('');
      setTime('');
      setReason('');
      setBuildings([]);
      setStudents([]);
      setStudentSearch('');
      setErrors({});

      // Fetch academic years dynamically to match Hostel Student tab
      const fetchAcademicYears = async () => {
        try {
          const token = getAuthToken();
          const baseUrl = getBaseUrl();
          const res = await axios.get(`${baseUrl}/student/dropdown-filters`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const data = res.data?.data || res.data || {};
          if (Array.isArray(data.academicYears) && data.academicYears.length > 0) {
            setAcademicYears(data.academicYears.filter(Boolean));
            return;
          }
        } catch (e) {
          // fallback to generated years
        }
        setAcademicYears(generateAcademicYears());
      };

      fetchAcademicYears();
    }
  }, [open]);

  // 1. Fetch Buildings based on Category
  useEffect(() => {
    if (!category) {
      setBuildings([]);
      setSelectedBuildingIds([]);
      return;
    }

    const fetchBuildings = async () => {
      setBuildingsLoading(true);
      try {
        const token = getAuthToken();
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/building/all-buildings?type=${encodeURIComponent(category)}`;

        const response = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        const result = response.data;
        if (response.status === 200 && (result.statusCode === 200 || result.success !== false)) {
          const rawList = Array.isArray(result)
            ? result
            : (result?.data?.buildings || result?.data || []);

          const sortedList = sortBuildings(rawList);
          setBuildings(sortedList);
        } else {
          setBuildings([]);
        }
      } catch (err) {
        console.error('Failed to fetch buildings for category:', err);
        setBuildings([]);
      } finally {
        setBuildingsLoading(false);
      }
    };

    fetchBuildings();
  }, [category]);

  // 2. Fetch Students when Category is selected
  // If user further selects Building, Department, or Academic Year, re-fetch accordingly
  useEffect(() => {
    if (!category) {
      setStudents([]);
      setSelectedStudentIds([]);
      setStudentSearch('');
      return;
    }

    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        const token = getAuthToken();
        const queryParams = new URLSearchParams();
        queryParams.append('category', category.trim());

        if (selectedBuildingIds.length > 0) {
          queryParams.append('buildingId', selectedBuildingIds.join(','));
        }
        if (selectedDepartments.length > 0) {
          queryParams.append('department', selectedDepartments.join(','));
        }
        if (selectedSessions.length > 0) {
          queryParams.append('session', selectedSessions.join(','));
        }

        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/gate_pass/students?${queryParams.toString()}`;
        console.log('Fetching students URL:', url);

        let studentList = [];
        try {
          const res = await axios.get(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          studentList = extractStudentList(res.data);
        } catch (axiosErr) {
          console.warn('Axios fetch students error, trying fetch fallback:', axiosErr);
          const response = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const result = await response.json();
          studentList = extractStudentList(result);
        }

        console.log('Fetched students count:', studentList.length);
        setStudents(studentList);
      } catch (err) {
        console.error('Failed to fetch students:', err);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };

    fetchStudents();
  }, [category, selectedBuildingIds, selectedDepartments, selectedSessions]);

  // Category Change Handler - selecting UG or PG immediately triggers student fetch
  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setCategory(val);
    setSelectedBuildingIds([]);
    setSelectedDepartments([]);
    setSelectedSessions([]);
    setSelectedStudentIds([]);
    setStudentSearch('');
    setErrors((prev) => ({ ...prev, category: '', studentIds: '' }));
  };

  // Building Multi-Select Calculation & Handler
  const allBuildingValues = useMemo(() => {
    return buildings.map(getBuildingValue).filter(Boolean);
  }, [buildings]);

  const isAllBuildingsSelected = buildings.length > 0 && selectedBuildingIds.length === buildings.length;
  const isBuildingsIndeterminate = selectedBuildingIds.length > 0 && selectedBuildingIds.length < buildings.length;

  const handleBuildingChange = (event) => {
    const value = event.target.value;
    if (value.includes('SELECT_ALL')) {
      if (isAllBuildingsSelected) {
        setSelectedBuildingIds([]);
      } else {
        setSelectedBuildingIds([...allBuildingValues]);
      }
      setSelectedStudentIds([]);
      return;
    }
    setSelectedBuildingIds(value);
    setSelectedStudentIds([]);
  };

  // Department Multi-Select Calculation & Handler
  const DEPARTMENT_OPTIONS = ['MBBS', 'BDS'];
  const isAllDepartmentsSelected = selectedDepartments.length === DEPARTMENT_OPTIONS.length;
  const isDepartmentsIndeterminate = selectedDepartments.length > 0 && selectedDepartments.length < DEPARTMENT_OPTIONS.length;

  const handleDepartmentChange = (event) => {
    const value = event.target.value;
    if (value.includes('SELECT_ALL')) {
      if (isAllDepartmentsSelected) {
        setSelectedDepartments([]);
      } else {
        setSelectedDepartments([...DEPARTMENT_OPTIONS]);
      }
      setSelectedStudentIds([]);
      return;
    }
    setSelectedDepartments(value);
    setSelectedStudentIds([]);
  };

  // Academic Year Multi-Select Calculation & Handler
  const isAllSessionsSelected = academicYears.length > 0 && selectedSessions.length === academicYears.length;
  const isSessionsIndeterminate = selectedSessions.length > 0 && selectedSessions.length < academicYears.length;

  const handleSessionChange = (event) => {
    const value = event.target.value;
    if (value.includes('SELECT_ALL')) {
      if (isAllSessionsSelected) {
        setSelectedSessions([]);
      } else {
        setSelectedSessions([...academicYears]);
      }
      setSelectedStudentIds([]);
      return;
    }
    setSelectedSessions(value);
    setSelectedStudentIds([]);
  };

  // Filtered students based on search query (name or UID)
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return students;
    const q = studentSearch.trim().toLowerCase();
    return students.filter((s) => {
      const name = getStudentName(s).toLowerCase();
      const uid = getStudentUID(s).toLowerCase();
      return name.includes(q) || uid.includes(q);
    });
  }, [students, studentSearch]);

  // Student Selection Handlers
  const filteredStudentIds = useMemo(() => {
    return filteredStudents.map(getStudentId).filter(Boolean);
  }, [filteredStudents]);

  const isAllSelected = students.length > 0 && selectedStudentIds.length === students.length;

  // When search query is active, check whether all visible/filtered students are selected
  const isAllFilteredSelected =
    filteredStudents.length > 0 &&
    filteredStudentIds.every((id) => selectedStudentIds.includes(id));

  const isFilteredIndeterminate =
    filteredStudentIds.some((id) => selectedStudentIds.includes(id)) &&
    !isAllFilteredSelected;

  const handleStudentSelect = (event) => {
    const value = event.target.value;

    if (value.includes('SELECT_ALL')) {
      if (isAllFilteredSelected) {
        // Deselect the filtered students while retaining any others
        setSelectedStudentIds((prev) => prev.filter((id) => !filteredStudentIds.includes(id)));
      } else {
        // Add all filtered students
        setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...filteredStudentIds])));
      }
      setErrors((prev) => ({ ...prev, studentIds: '' }));
      return;
    }

    setSelectedStudentIds(value);
    setErrors((prev) => ({ ...prev, studentIds: '' }));
  };

  // Form Validation - ONLY Category, Student List, Date, and Time are required
  const validateForm = () => {
    const newErrors = {};

    if (!category) newErrors.category = 'Category is required';
    if (!selectedStudentIds || selectedStudentIds.length === 0) {
      newErrors.studentIds = 'Please select at least one student';
    }
    if (!date) {
      newErrors.date = 'Date is required';
    } else if (date > todayStr) {
      newErrors.date = 'Future dates are not allowed';
    }
    if (!time) newErrors.time = 'Time is required';
    if (!reason || !reason.trim()) newErrors.reason = 'Reason is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      studentIds: selectedStudentIds,
      date,
      time,
      statusReason: reason.trim()
    };

    setSubmitting(true);
    try {
      const token = getAuthToken();
      const baseUrl = getBaseUrl();
      const url = `${baseUrl}/gate_pass/mark-late-entry`;

      const response = await axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      const result = response.data;
      if (
        response.status === 200 ||
        response.status === 201 ||
        result.statusCode === 200 ||
        result.success !== false
      ) {
        const data = result.data || {};
        const skipped = Array.isArray(data.skipped) ? data.skipped : [];
        const markedGreen = Array.isArray(data.markedGreen) ? data.markedGreen : [];
        const skippedCount =
          typeof data.skippedCount === 'number' ? data.skippedCount : skipped.length;
        const markedGreenCount =
          typeof data.markedGreenCount === 'number' ? data.markedGreenCount : markedGreen.length;

        if (
          skipped.length > 0 ||
          markedGreen.length > 0 ||
          skippedCount > 0 ||
          markedGreenCount > 0
        ) {
          toast(
            <GPExtensionResultToast
              markedGreenCount={markedGreenCount}
              skippedCount={skippedCount}
              markedGreen={markedGreen}
              skipped={skipped}
              studentsList={students}
              message={result.message}
            />,
            {
              autoClose: false,
              hideProgressBar: true,
              closeOnClick: true,
              containerId: 'uploadResults',
              className: 'uploadToast'
            }
          );
        } else {
          toast.success(result.message || 'GP Extension marked successfully');
        }

        if (typeof onRefresh === 'function') {
          onRefresh();
        }
        onClose();
      } else {
        toast.error(result.message || 'Failed to mark GP Extension');
      }
    } catch (error) {
      console.error('Error marking GP extension:', error);
      const errMsg =
        error.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: styles.modalPaper
      }}
    >
      {/* Modal Header */}
      <DialogTitle className={styles.modalHeader}>
        <Typography variant="h6" className={styles.modalTitle}>
          GP Extension
        </Typography>
        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          size="small"
          disabled={submitting}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Modal Body / Content */}
      <DialogContent className={styles.modalContent}>
        <Grid container spacing={2.5}>
          {/* 1. Category Field (Required) */}
          <Grid item xs={12} sm={6}>
            <Typography component="div" className={styles.fieldLabel}>
              Category <span className={styles.requiredStar}>*</span>
            </Typography>
            <FormControl fullWidth size="small" error={!!errors.category}>
              <Select
                value={category}
                onChange={handleCategoryChange}
                displayEmpty
                MenuProps={DROPDOWN_MENU_PROPS}
                sx={selectSx}
                renderValue={(selected) => {
                  if (!selected) return <span style={{ color: '#94a3b8' }}>Select Category</span>;
                  return selected.toUpperCase();
                }}
              >
                <MenuItem value="" disabled>
                  <span style={{ color: '#94a3b8' }}>Select Category</span>
                </MenuItem>
                <MenuItem value="UG">UG</MenuItem>
                <MenuItem value="PG">PG</MenuItem>
              </Select>
              {errors.category && <p className={styles.errorText}>{errors.category}</p>}
            </FormControl>
          </Grid>

          {/* 2. Building Field (Optional Filter, Multi-select) */}
          <Grid item xs={12} sm={6}>
            <Typography component="div" className={styles.fieldLabel}>
              Building <span className={styles.optionalText}></span>
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedBuildingIds}
                onChange={handleBuildingChange}
                disabled={!category || buildingsLoading}
                displayEmpty
                MenuProps={DROPDOWN_MENU_PROPS}
                sx={selectSx}
                renderValue={(selected) => {
                  if (!category) return <span style={{ color: '#94a3b8' }}>Select Category First</span>;
                  if (buildingsLoading) return 'Loading Buildings...';
                  if (!selected || selected.length === 0) return <span style={{ color: '#94a3b8' }}>All Buildings</span>;
                  if (selected.length === buildings.length) return `All Buildings (${buildings.length})`;
                  if (selected.length === 1) {
                    const b = buildings.find(
                      (item) => getBuildingValue(item) === String(selected[0])
                    );
                    return b ? getBuildingLabel(b) : selected[0];
                  }
                  return `${selected.length} Buildings Selected`;
                }}
              >
                {/* Select All Buildings */}
                {buildings.length > 0 && (
                  <MenuItem
                    value="SELECT_ALL"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: isAllBuildingsSelected ? '#f5f3ff' : 'inherit',
                      borderBottom: '1px solid #f1f5f9',
                      py: 0.8
                    }}
                  >
                    <Checkbox
                      checked={isAllBuildingsSelected}
                      indeterminate={isBuildingsIndeterminate}
                      size="small"
                      sx={{
                        p: 0.5,
                        mr: 1,
                        color: '#644ee5',
                        '&.Mui-checked': { color: '#644ee5' },
                        '&.MuiCheckbox-indeterminate': { color: '#644ee5' }
                      }}
                    />
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: '#644ee5' }}>
                          Select All Buildings ({buildings.length})
                        </Typography>
                      }
                    />
                  </MenuItem>
                )}

                {buildings.map((b) => {
                  const bId = getBuildingValue(b);
                  const label = getBuildingLabel(b);
                  const isChecked = selectedBuildingIds.includes(bId);
                  return (
                    <MenuItem key={bId} value={bId} sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 1,
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: '#644ee5' }
                        }}
                      />
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '13.5px', color: '#1e293b' }}>{label}</Typography>}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* 3. Department Field (Optional Filter, Multi-select) */}
          <Grid item xs={12} sm={6}>
            <Typography component="div" className={styles.fieldLabel}>
              Department <span className={styles.optionalText}></span>
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                disabled={!category}
                displayEmpty
                MenuProps={DROPDOWN_MENU_PROPS}
                sx={selectSx}
                renderValue={(selected) => {
                  if (!category) return <span style={{ color: '#94a3b8' }}>Select Category First</span>;
                  if (!selected || selected.length === 0) return <span style={{ color: '#94a3b8' }}>All Departments</span>;
                  if (selected.length === DEPARTMENT_OPTIONS.length) return `All Departments (${DEPARTMENT_OPTIONS.length})`;
                  if (selected.length === 1) return selected[0];
                  return `${selected.length} Departments Selected`;
                }}
              >
                {/* Select All Departments */}
                <MenuItem
                  value="SELECT_ALL"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: isAllDepartmentsSelected ? '#f5f3ff' : 'inherit',
                    borderBottom: '1px solid #f1f5f9',
                    py: 0.8
                  }}
                >
                  <Checkbox
                    checked={isAllDepartmentsSelected}
                    indeterminate={isDepartmentsIndeterminate}
                    size="small"
                    sx={{
                      p: 0.5,
                      mr: 1,
                      color: '#644ee5',
                      '&.Mui-checked': { color: '#644ee5' },
                      '&.MuiCheckbox-indeterminate': { color: '#644ee5' }
                    }}
                  />
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: '#644ee5' }}>
                        Select All Departments ({DEPARTMENT_OPTIONS.length})
                      </Typography>
                    }
                  />
                </MenuItem>

                {DEPARTMENT_OPTIONS.map((dept) => {
                  const isChecked = selectedDepartments.includes(dept);
                  return (
                    <MenuItem key={dept} value={dept} sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 1,
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: '#644ee5' }
                        }}
                      />
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '13.5px', color: '#1e293b' }}>{dept}</Typography>}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* 4. Academic Year / Session Field (Optional Filter, Multi-select) */}
          <Grid item xs={12} sm={6}>
            <Typography component="div" className={styles.fieldLabel}>
              Academic Year <span className={styles.optionalText}></span>
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedSessions}
                onChange={handleSessionChange}
                disabled={!category}
                displayEmpty
                MenuProps={DROPDOWN_MENU_PROPS}
                sx={selectSx}
                renderValue={(selected) => {
                  if (!category) return <span style={{ color: '#94a3b8' }}>Select Category First</span>;
                  if (!selected || selected.length === 0) return <span style={{ color: '#94a3b8' }}>All Academic Years</span>;
                  if (selected.length === academicYears.length) return `All Academic Years (${academicYears.length})`;
                  if (selected.length === 1) return selected[0];
                  return `${selected.length} Academic Years Selected`;
                }}
              >
                {/* Select All Academic Years */}
                {academicYears.length > 0 && (
                  <MenuItem
                    value="SELECT_ALL"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: isAllSessionsSelected ? '#f5f3ff' : 'inherit',
                      borderBottom: '1px solid #f1f5f9',
                      py: 0.8
                    }}
                  >
                    <Checkbox
                      checked={isAllSessionsSelected}
                      indeterminate={isSessionsIndeterminate}
                      size="small"
                      sx={{
                        p: 0.5,
                        mr: 1,
                        color: '#644ee5',
                        '&.Mui-checked': { color: '#644ee5' },
                        '&.MuiCheckbox-indeterminate': { color: '#644ee5' }
                      }}
                    />
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: '#644ee5' }}>
                          Select All Academic Years ({academicYears.length})
                        </Typography>
                      }
                    />
                  </MenuItem>
                )}

                {academicYears.map((yearStr) => {
                  const isChecked = selectedSessions.includes(yearStr);
                  return (
                    <MenuItem key={yearStr} value={yearStr} sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 1,
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: '#644ee5' }
                        }}
                      />
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '13.5px', color: '#1e293b' }}>{yearStr}</Typography>}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* 5. Student List Multi-Select Field with Search (Required) */}
          <Grid item xs={12}>
            <Typography component="div" className={styles.fieldLabel}>
              Student List <span className={styles.requiredStar}>*</span>
            </Typography>
            <FormControl fullWidth size="small" error={!!errors.studentIds}>
              <Select
                multiple
                value={selectedStudentIds}
                onChange={handleStudentSelect}
                disabled={!category || studentsLoading}
                displayEmpty
                onClose={() => setStudentSearch('')}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    sx: {
                      maxHeight: 520,
                      minHeight: 360,
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                    }
                  },
                  MenuListProps: {
                    sx: { pt: 0, pb: 0 }
                  }
                }}
                sx={selectSx}
                renderValue={(selected) => {
                  if (!category) return <span style={{ color: '#94a3b8' }}>Select Category First</span>;
                  if (studentsLoading) return 'Loading Students...';
                  if (students.length === 0) return <span style={{ color: '#94a3b8' }}>No Students Found</span>;
                  if (!selected || selected.length === 0) return <span style={{ color: '#94a3b8' }}>Select Student(s)</span>;
                  if (isAllSelected) return `All Students Selected (${students.length})`;
                  if (selected.length === 1) {
                    const s = students.find(
                      (item) => getStudentId(item) === String(selected[0])
                    );
                    const sName = getStudentName(s);
                    const sUID = getStudentUID(s);
                    return sUID ? `${sName} (${sUID})` : sName;
                  }
                  return `${selected.length} Students Selected`;
                }}
              >
                {/* Search Header inside dropdown */}
                {students.length > 0 && (
                  <ListSubheader
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#ffffff',
                      pt: 1,
                      pb: 1,
                      px: 1.5,
                      borderBottom: '1px solid #f1f5f9'
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search student by name or UID..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: '8px',
                          fontSize: '13px',
                          height: '38px',
                          backgroundColor: '#f8fafc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#cbd5e1'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#644ee5',
                            borderWidth: '1.5px'
                          }
                        }
                      }}
                    />
                  </ListSubheader>
                )}

                {/* Select All Option */}
                {filteredStudents.length > 0 && (
                  <MenuItem
                    value="SELECT_ALL"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: isAllFilteredSelected ? '#f5f3ff' : 'inherit',
                      borderBottom: '1px solid #f1f5f9',
                      py: 1
                    }}
                  >
                    <Checkbox
                      checked={isAllFilteredSelected}
                      indeterminate={isFilteredIndeterminate}
                      size="small"
                      sx={{
                        p: 0.5,
                        mr: 1,
                        color: '#644ee5',
                        '&.Mui-checked': { color: '#644ee5' },
                        '&.MuiCheckbox-indeterminate': { color: '#644ee5' }
                      }}
                    />
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: '#644ee5' }}>
                          {studentSearch.trim()
                            ? `Select All Filtered (${filteredStudents.length})`
                            : `Select All Students (${students.length})`}
                        </Typography>
                      }
                    />
                  </MenuItem>
                )}

                {/* No matching results for search */}
                {filteredStudents.length === 0 && students.length > 0 && (
                  <MenuItem disabled sx={{ py: 2, justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>
                      No students match &quot;{studentSearch}&quot;
                    </Typography>
                  </MenuItem>
                )}

                {/* Individual Student Items */}
                {filteredStudents.map((s) => {
                  const sId = getStudentId(s);
                  const sName = getStudentName(s);
                  const sUID = getStudentUID(s);
                  const isChecked = selectedStudentIds.includes(sId);

                  return (
                    <MenuItem key={sId} value={sId} sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 1,
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: '#644ee5' }
                        }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          sx={{
                            fontSize: '13.5px',
                            fontWeight: isChecked ? 600 : 500,
                            color: '#1e293b',
                            textTransform: 'uppercase'
                          }}
                        >
                          {sName}
                        </Typography>
                        {sUID && (
                          <Typography sx={{ fontSize: '11.5px', color: '#64748b' }}>
                            UID: {sUID}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
              {errors.studentIds && <p className={styles.errorText}>{errors.studentIds}</p>}
            </FormControl>
          </Grid>

          {/* 6. Date Picker */}
          <Grid item xs={12} sm={6}>
            <Typography component="div" className={styles.fieldLabel}>
              Extension Date <span className={styles.requiredStar}>*</span>
            </Typography>
            <input
              type="date"
              max={todayStr}
              className={`${styles.dateInput} ${errors.date ? styles.inputError : ''}`}
              value={date}
              onChange={(e) => {
                const val = e.target.value;
                if (val && val > todayStr) {
                  setDate(todayStr);
                  setErrors((prev) => ({ ...prev, date: 'Future dates are not allowed' }));
                } else {
                  setDate(val);
                  setErrors((prev) => ({ ...prev, date: '' }));
                }
              }}
            />
            {errors.date && <p className={styles.errorText}>{errors.date}</p>}
          </Grid>

          {/* 7. Time Picker */}
          <Grid item xs={12} sm={6}>
            <Typography component="div" className={styles.fieldLabel}>
              Extension Time <span className={styles.requiredStar}>*</span>
            </Typography>
            <input
              type="time"
              className={`${styles.timeInput} ${errors.time ? styles.inputError : ''}`}
              value={time}
              onChange={(e) => {
                setTime(e.target.value);
                setErrors((prev) => ({ ...prev, time: '' }));
              }}
            />
            {errors.time && <p className={styles.errorText}>{errors.time}</p>}
          </Grid>

          {/* 8. Reason Box (Required) */}
          <Grid item xs={12}>
            <Typography component="div" className={styles.fieldLabel}>
              Reason <span className={styles.requiredStar}>*</span>
            </Typography>
            <textarea
              rows={3}
              className={`${styles.textareaInput} ${errors.reason ? styles.inputError : ''}`}
              placeholder="Enter reason for GP extension..."
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setErrors((prev) => ({ ...prev, reason: '' }));
              }}
            />
            {errors.reason && <p className={styles.errorText}>{errors.reason}</p>}
          </Grid>
        </Grid>
      </DialogContent>

      {/* Modal Actions */}
      <DialogActions className={styles.modalActions}>
        <Button
          onClick={onClose}
          variant="outlined"
          className={styles.cancelBtn}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className={styles.submitBtn}
          disabled={submitting}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Mark GP Extension'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GPExtensionModal;
